import {
	getClassroomAnalyticsDatatable,
	getClassroomEnrollments,
	getClassroomGraph,
} from '@Features/classrooms/classroomSlice';
import { useTypedSelector } from '@Features/store';
import {
	Icon,
	Button,
	Box,
	TableCell,
	TableRow,
	MenuItem,
	Checkbox,
} from '@mui/material';
import { deepPurple, green, orange, red, yellow } from '@mui/material/colors';
import {
	ClassroomGraphPayload,
	CourseEnrollment,
	EnrollmentType,
	GraphSlice,
	GraphSliceType,
} from '@Services/classrooms/classrooms.res.types';
import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import MultipleLinesChart from '@Components/UI/Charts/MultipleLinesChart/MultipleLinesChart';
import { object, mixed, array, number } from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FlexItem, FlexLayout, Spacer } from '@Styled/utilities';
import MonthRangePicker from '@Components/UI/Pickers/MonthRangePicker/MonthRangePicker';
import YearRangePicker from '@Components/UI/Pickers/YearRangePicker/YearRangePicker';
import '@mui/lab';
import DateRangePickerPopup, {
	DateRangeOption,
} from '@Components/UI/Pickers/DateRangePickerPopup/DateRangePickerPopup';

import { useParams } from 'react-router-dom';
import { fetchCoursesDropdown } from '@Features/dropdowns/dropdownsSlice';
import {
	AnalyticsCardsContainer,
	EnrollmentsCard,
	TypeInfo,
	EnrollmentTypeCard,
	TypeValue,
	TypeName,
	enrollmentIcons,
	FormControlsWrapper,
	FormControlLabel,
	StyledIconButton,
} from '@Components/UI/Analytics/Analytics.styled';
import { TableHeader } from '@Features/generics/generics.type';
import { exportClassroomAnalytics } from '@Features/classrooms/exportSlice';
import MainTable from '@Components/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import { AnalyticsTotal } from '@Components/UI/Analytics/Assets/Icons/Icons';
import AddClassroomButton from '@Pages/ClassRooms/Components/AddClassroomButton';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import SelectChip from '@Components/UI/Inputs/SelectChip/SelectChip';
import { AnalyticsSlices } from '@Pages/ClassRooms/Constants/Classroom.constants';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import ClassroomUnitsAnalytics from './ClassroomUnitsAnalytics/ClassroomUnitsAnalytics';

const SCHEMA = object().shape({
	slice: mixed<GraphSliceType>().required(),
	classroom_ids: array().of(number()),
	dates: array(),
});

type FormType = {
	slice: GraphSliceType;
	course_ids: number[];
	dates: any;
};
// fieldNames match the backend's JS-sort accessor map.
const TABLE_HEADS: TableHeader[] = [
	{
		displayName: 'Course',
		fieldName: 'course_name',
		width: '18%',
	},
	{ displayName: 'Price', fieldName: 'price' },
	{ displayName: 'Wallet', fieldName: 'wallet' },
	{ displayName: 'ScratchCards', fieldName: 'scratchcard' },
	{ displayName: 'Free', fieldName: 'free' },
	{ displayName: 'Admin', fieldName: 'admin' },
	{ displayName: 'Instructor', fieldName: 'instructor' },
	{ displayName: 'Total', fieldName: 'total' },
	{ displayName: 'Created On', fieldName: 'created_at', width: '12%' },
];

/** Read a method's count by name — positional indexing broke as soon as the
 *  row shape gained fields. */
const methodCount = (en: CourseEnrollment, method: string) =>
	en.purchase_methods?.find((p) => p.purchase_method === method)
		?.purchase_method_count ?? 0;

const ClassroomAnalytics: React.FC = () => {
	const dispatch = useDispatch();
	const {
		analytics: {
			currentClassroom: {
				enrollments,
				graph,
				datatable: { data, filters, meta },
			},
		},
		currentClassroom,
	} = useTypedSelector((state) => state.classroom);
	const { dropdownCourses } = useTypedSelector((state) => state.dropdowns);
	const auth = useTypedSelector((state) => state.auth);
	// Last args the table fetched with — the Export button reuses them, since
	// the old doExport flag only fired from the page-change callback and
	// nothing re-triggered it after the flag flipped.
	const lastArgsRef = useRef<onPageChangeRequestMeta | null>(null);

	const defaultValues = useMemo<FormType>(() => {
		const startDate = new Date();
		startDate.setDate(startDate.getDate() - 14);
		return {
			course_ids: [],
			slice: 'day',
			dates: [startDate, new Date()],
		};
	}, []);
	const {
		control,
		watch,
		register,
		setValue,
		reset,
		handleSubmit,
		getValues,
		formState: { isValid },
	} = useForm<FormType>({
		resolver: yupResolver(SCHEMA),
		defaultValues: defaultValues,
		mode: 'all',
	});

	useEffect(() => {
		register('dates');
		if (!currentClassroom) return;
		dispatch(fetchCoursesDropdown(currentClassroom.id));
		// Initial unbounded KPI load; range-scoped refreshes come from the
		// table's date bar via handleDatatableRequest below.
		dispatch(getClassroomEnrollments({ classroom_id: currentClassroom.id }));
		dispatch(
			getClassroomGraph({
				from: defaultValues.dates[0],
				to: defaultValues.dates[1],
				slice: 'day',
				course_ids: [],
				classroom_id: currentClassroom.id,
			})
		);
	}, [currentClassroom?.id]);

	const pickTypeColor = useCallback((type: EnrollmentType) => {
		if (type === 'admin') {
			return red[500];
		}
		if (type === 'wallet') {
			return deepPurple[500];
		}
		if (type === 'scratchcard') {
			return orange[500];
		}
		if (type === 'free') {
			return yellow[600];
		}
		if (type === 'instructor') {
			return green['A700'];
		}
	}, []);

	const getGraph = useCallback((args: ClassroomGraphPayload) => {
		dispatch(getClassroomGraph(args));
	}, []);

	const watchSliceType = watch('slice');
	const watchCourses = watch('course_ids');

	const handleFilterForm = (data: FormType) => {
		if (currentClassroom) {
			getGraph({
				course_ids: data.course_ids,
				slice: data.slice,
				from: data.dates[0],
				to: data.dates[1],
				classroom_id: currentClassroom?.id,
			});
		}
	};

	const [dateRange, setDateRangeOption] =
		useState<DateRangeOption>('last 2 weeks');

	const handleDateRangeChange = (type: DateRangeOption) => {
		setDateRangeOption(type);

		if (type === 'custom') return;
		const startDate = new Date();
		const endDate = new Date();
		if (type === 'last 2 weeks') {
			startDate.setDate(startDate.getDate() - 14);
		}
		if (type === 'last month') {
			startDate.setMonth(startDate.getMonth() - 1);
		}
		if (type === 'last 3 months') {
			startDate.setMonth(startDate.getMonth() - 2);
		}
		setValue('dates', [startDate, endDate], {
			shouldValidate: true,
			shouldDirty: true,
		});
	};

	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLElement>();

	const handleDatatableRequest = useCallback(
		(meta: onPageChangeRequestMeta) => {
			if (!currentClassroom) return;
			lastArgsRef.current = meta;
			const { activePage, endDate, filters, query, sortBy, startDate, perPage } =
				meta;
			dispatch(
				getClassroomAnalyticsDatatable({
					page: activePage,
					perPage: perPage,
					filters: filters,
					from: startDate,
					to: endDate,
					sortBy: sortBy,
					classroom_id: currentClassroom.id,
					query: query,
				})
			);
			// The KPI cards follow the same date range as the table.
			dispatch(
				getClassroomEnrollments({
					classroom_id: currentClassroom.id,
					from: startDate,
					to: endDate,
				})
			);
		},
		[currentClassroom]
	);

	const handleExport = () => {
		if (!currentClassroom) return;
		const args = lastArgsRef.current;
		dispatch(
			exportClassroomAnalytics({
				page: args?.activePage ?? 1,
				filters: args?.filters,
				perPage: args?.perPage ?? 10,
				classroom_id: currentClassroom.id,
				from: args?.startDate,
				to: args?.endDate,
				query: args?.query,
				sortBy: args?.sortBy,
			})
		);
	};

	const onDeleteValue = (control: 'course_ids', value: number) => {
		const values = getValues()[control];
		if (!values) return;
		values.splice(
			values.findIndex((_) => _ === value),
			1
		);
		setValue(control, values);
	};

	const resetForm = () => {
		if (!currentClassroom) return;
		reset({
			course_ids: [],
			slice: 'day',
			dates: defaultValues.dates,
		});
		setDateRangeOption('last 2 weeks');
		register('dates');
		getGraph({
			classroom_id: currentClassroom.id,
			course_ids: [],
			slice: 'day',
			from: defaultValues.dates[0],
			to: defaultValues.dates[1],
		});
	};
	return (
		<>
			{enrollments && (
				<AnalyticsCardsContainer>
					<EnrollmentsCard elevation={10}>
						<AnalyticsTotal />

						<TypeInfo>
							<span>{enrollments.purchase_methods_total}</span>
							<label>Enrollments</label>
						</TypeInfo>
					</EnrollmentsCard>
					{enrollments.purchase_methods.map((en) => {
						return (
							<EnrollmentTypeCard key={en.purchase_method} color={pickTypeColor(en.purchase_method)}>
								<TypeInfo>
									{enrollmentIcons[en.purchase_method]}
									<TypeValue>{en.purchase_method_count}</TypeValue>
								</TypeInfo>
								<TypeName>{en.purchase_method}</TypeName>
							</EnrollmentTypeCard>
						);
					})}
				</AnalyticsCardsContainer>
			)}

			<Box width="100%" display="flex" justifyContent="flex-end">
				<RoundButton
					onClick={handleExport}
					startIcon={<Icon>south</Icon>}
					variant="contained"
				>
					Export
				</RoundButton>
				<Spacer mx={'0.625rem'} />
				{(auth.user.type === 'super' || auth.permissions.has('c-cl')) && (
					<AddClassroomButton />
				)}
			</Box>

			<MainTable
				tableFilters={filters as Array<any>}
				RowsperPage={meta?.per_page}
				onPageChangeRequest={handleDatatableRequest}
				totalPages={meta?.last_page ?? 0}
				total={meta?.total}
				pageNum={meta?.current_page}
				tableHeads={TABLE_HEADS}
				renderItems={() => {
					if (data.length <= 0)
						return (
							<TableRow>
								<TableCell colSpan={TABLE_HEADS.length}>
									<NoResults />
								</TableCell>
							</TableRow>
						);
					return data.map((en) => {
						return (
							<TableRow>
								<WrappedTableCell align="center">
									{en.course_name}
								</WrappedTableCell>
								<TableCell align="center">{en.price ?? '-'}</TableCell>
								<TableCell align="center">
									{methodCount(en, 'wallet')}
								</TableCell>
								<TableCell align="center">
									{methodCount(en, 'scratchcard')}
								</TableCell>
								<TableCell align="center">{methodCount(en, 'free')}</TableCell>
								<TableCell align="center">
									{methodCount(en, 'admin')}
								</TableCell>
								<TableCell align="center">
									{methodCount(en, 'instructor')}
								</TableCell>
								<TableCell align="center">
									{en.purchase_methods_total}
								</TableCell>
								<TableCell align="center">
									{en.created_at ? (
										<DateTimeFormatter date={new Date(en.created_at)} />
									) : (
										'-'
									)}
								</TableCell>
							</TableRow>
						);
					});
				}}
			/>

			<FormControlsWrapper>
				<FormControlLabel>Select Course(s)</FormControlLabel>

				<EdFormControl
					width="12rem"
					select
					multiple
					SelectProps={{
						multiple: true,
						renderValue: () => {
							return (
								<>
									{watchCourses.map((_) => {
										const cl = dropdownCourses.find((cl) => _ === cl.id);
										return <>{cl?.name},</>;
									})}
								</>
							);
						},
					}}
					label={''}
					name="course_ids"
					control={control}
				>
					{dropdownCourses &&
						dropdownCourses.map((_, index) => {
							return (
								<MenuItem value={_.id} key={`${index}-${_.id}`}>
									<Checkbox checked={watchCourses?.includes(_.id)} />
									<div>{_.name}</div>
								</MenuItem>
							);
						})}
				</EdFormControl>
				<Spacer mx={14} />

				<EdFormControl select name="slice" control={control}>
					{AnalyticsSlices.map((_) => {
						return (
							<MenuItem key={_.value} value={_.value}>
								{_.label}
							</MenuItem>
						);
					})}
				</EdFormControl>
				<Spacer mx={14} />

				<StyledIconButton
					onClick={(e: React.MouseEvent<HTMLElement>) => {
						setOpen(!open);
						setAnchorEl(e.currentTarget);
					}}
				>
					<Icon>event</Icon>
				</StyledIconButton>
				<DateRangePickerPopup
					open={open}
					anchorEl={anchorEl}
					onClose={() => {
						setOpen(false);
					}}
					onChange={handleDateRangeChange}
					rangeOptionValue={dateRange}
				>
					<>
						<DateRangePickerPopup.Body>
							{(watchSliceType === 'day' || watchSliceType === 'week') && (
								<Controller
									control={control}
									name="dates"
									render={({ field: { value, onChange, ref, onBlur } }) => {
										return (
											<div></div>
											// <StaticDateRangePicker
											//   calendars={2}
											//   showToolbar={false}
											//   onChange={onChange}
											//   value={value}
											//   ref={ref}
											//   renderInput={(
											//     startProps: TextFieldProps,
											//     endProps: TextFieldProps
											//   ) => (
											//     <>
											//       <TextField {...startProps} />
											//       <TextField {...endProps} />
											//     </>
											//   )}
											// />
										);
									}}
								/>
							)}

							{watchSliceType === 'month' && (
								<MonthRangePicker control={control as any} name="dates" />
							)}
							{watchSliceType === 'year' && (
								<YearRangePicker control={control as any} name="dates" />
							)}
						</DateRangePickerPopup.Body>
					</>
				</DateRangePickerPopup>
			</FormControlsWrapper>
			<FlexLayout alignItems="flex-end">
				<FlexItem flex="1">
					<>
						{watchCourses.map((_) => {
							const cl = dropdownCourses.find((cl) => cl.id === _);
							return (
								<SelectChip
									onDelete={() => onDeleteValue('course_ids', _)}
									label={cl?.name}
								/>
							);
						})}
					</>
				</FlexItem>
				<FlexLayout>
					<Button onClick={() => resetForm()} variant="outlined">
						Reset
					</Button>
					<Spacer mx={'1rem'} />
					<Button
						disabled={!isValid}
						onClick={handleSubmit(handleFilterForm)}
						variant="contained"
					>
						Apply
					</Button>
				</FlexLayout>
			</FlexLayout>

			<MultipleLinesChart
				sliceType={watchSliceType}
				colors={(slice: GraphSlice) => {
					return pickTypeColor(slice.id as EnrollmentType);
				}}
				data={graph}
			/>
			<ClassroomUnitsAnalytics />
		</>
	);
};

export default ClassroomAnalytics;
